import { FC, ReactNode } from "react";
import { FieldArray, FieldArrayRenderProps, useFormikContext } from "formik";
import { get } from "@/lib/Utils/get";
import { PlusIcon } from "@heroicons/react/24/solid";
import { TrashIcon } from "@heroicons/react/24/outline";
import { IconButton } from "../../Button/IconButton";
import { PackageIcon } from "lucide-react";
import { Button } from "@/components/catalyst/button";
import { useFormEditState } from "@/lib/Components/Form/Hooks/useFormEditState";
import { cn } from "@/lib/utils";

export interface FieldArrayHeaderProps<TData = any> {
  index: number;
  item: TData;
  fieldHelpers: FieldArrayRenderProps;
  fieldArrayKey: string;
  onRemove?: (index: number) => void;
}

type FieldArrayProps<T extends object> = {
  label?: ReactNode;
  children: (item: T, index: number, isEditing: boolean) => ReactNode;
  viewChildren?: ReactNode;
  fieldArrayKey: string;
  lineItemLabel?: string;
  newLineItemValues: T;
  footer?: ReactNode;
  Header?: FC<FieldArrayHeaderProps<T>>;
  addLineItemButton?: ({
    fieldHelpers,
  }: {
    fieldHelpers: FieldArrayRenderProps;
  }) => ReactNode;
  emptyState?: ReactNode;
  Icon?: FC<any>;
  className?: string;
  isDeletable?: (item: T) => boolean;
};

export function GenericFieldArray<T extends object>({
  label,
  children,
  fieldArrayKey,
  newLineItemValues,
  footer,
  Header,
  addLineItemButton,
  emptyState,
  lineItemLabel = "line item",
  viewChildren,
  Icon = PackageIcon,
  className,
  isDeletable,
}: FieldArrayProps<T>) {
  const { values, setFieldValue } = useFormikContext();
  const { isEditing } = useFormEditState();

  const items = get(values, fieldArrayKey, []) as T[];

  // Early return for view mode
  if (!isEditing && viewChildren) {
    return viewChildren;
  }

  // Common styling constants
  const borderClasses = "border-gray-100";
  const paddingClasses = "p-4";

  // Render empty state
  const renderEmptyState = () =>
    emptyState || (
      <div className="flex flex-col items-center justify-center rounded-lg bg-gray-50 p-6 text-center">
        <Icon className="h-8 w-8 text-gray-400" />
        <p className="mt-2 text-sm text-gray-500">
          No {lineItemLabel}s available
        </p>
      </div>
    );

  return (
    <FieldArray name={fieldArrayKey}>
      {(fieldHelpers: FieldArrayRenderProps) => (
        <div className="col-span-full">
          {label && (
            <h2 className="mb-2 flex items-center text-sm font-medium text-gray-800">
              {label}
            </h2>
          )}
          <div className="shadow-xs overflow-hidden rounded-xl border border-gray-200 bg-white">
            {/* Items list */}
            {items.length > 0 && (
              <ul className="divide-y divide-gray-100">
                {items.map((item: T, index: number) => (
                  <li
                    key={getItemKey(fieldArrayKey, item, index)}
                    className="group transition-colors hover:bg-gray-50"
                  >
                    {Header ? (
                      <Header
                        index={index}
                        item={item}
                        fieldArrayKey={fieldArrayKey}
                        fieldHelpers={fieldHelpers}
                      />
                    ) : (
                      <div className="flex items-center p-3 text-xs font-semibold uppercase tracking-wider text-gray-600">
                        <div className="flex grow items-center space-x-4">
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-indigo-600">
                            {index + 1}
                          </span>
                          <span>
                            {lineItemLabel} {index + 1}
                          </span>
                        </div>
                        {isEditing &&
                        (isDeletable ? isDeletable?.(item) : true) ? (
                          <IconButton
                            tooltip="Remove"
                            Icon={TrashIcon}
                            onClick={() => {
                              // Important: Remove first, then handle deletion logic
                              fieldHelpers.remove(index);

                              // Handle the delete list if item has an ID
                              const deleteKey = fieldArrayKey.replace(
                                "upsert",
                                "delete",
                              );
                              const val = get(values, deleteKey, []);

                              if (item && "id" in item) {
                                const id = (item as any).id;
                                setFieldValue(deleteKey, [...val, id]);
                              }
                            }}
                            className="opacity-0 transition-opacity group-hover:opacity-100"
                          />
                        ) : null}
                      </div>
                    )}
                    <div className={paddingClasses}>
                      <div
                        className={cn(
                          "grid w-full grid-cols-3 gap-6 sm:grid-cols-12",
                          className,
                        )}
                      >
                        {children(item, index, isEditing)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {/* Add item section */}
            {isEditing ? (
              <div className={`border-t ${borderClasses} ${paddingClasses}`}>
                {items.length === 0 ? (
                  <button
                    type="button"
                    className="focus:outline-hidden relative flex w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-8 text-center transition-all hover:border-indigo-200 hover:bg-indigo-50 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={async () => {
                      fieldHelpers.push({ ...newLineItemValues });
                    }}
                  >
                    <Icon className="h-10 w-10 text-indigo-400" />
                    <span className="mt-2 block text-sm font-medium text-gray-900">
                      Add {lineItemLabel}
                    </span>
                  </button>
                ) : addLineItemButton ? (
                  addLineItemButton({ fieldHelpers })
                ) : (
                  <Button
                    outline
                    onClick={async () => {
                      //?? if this function is not async it adds two items :|
                      return fieldHelpers.push({ ...newLineItemValues });
                    }}
                  >
                    <PlusIcon />
                    Add {lineItemLabel}
                  </Button>
                )}
              </div>
            ) : (
              items.length === 0 && (
                <div className={paddingClasses}>{renderEmptyState()}</div>
              )
            )}

            {/* Footer */}
            {footer ? (
              <div className={`border-t ${borderClasses} ${paddingClasses}`}>
                {footer}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </FieldArray>
  );
}

/**
 * Rows keyed by index remount whenever an earlier row is removed, which drops
 * focus and any input-local state. Persisted rows carry an id, so use it.
 */
function getItemKey(fieldArrayKey: string, item: unknown, index: number) {
  if (item && typeof item === "object" && "id" in item && item.id) {
    return `${fieldArrayKey}-id-${String(item.id)}`;
  }

  return `${fieldArrayKey}-index-${index}`;
}
